// Submitting a loop: title, the two declarations (own work, CC BY 4.0), the check status with
// plain-language reasons, and the play time once approved and frozen.
import { useEffect, useState } from "react";
import { checkTitle, dayToDate, everyEvent, freezeAtMs } from "@eb/shared";
import { ApiError } from "../api";
import { Link, navigate } from "../lib/router";
import { formatPlayTime } from "../river/slots";
import { useSession } from "../session";
import { saveServerDraft, submitServerDraft, watchReview } from "./serverDraft";
import { useEditor } from "./store";
import { useWizard } from "./wizardClient";

export function SubmitPanel({ epochMs }: { epochMs: number }) {
  const user = useSession((s) => s.user);
  const server = useEditor((s) => s.server);
  const def = useEditor((s) => s.def);
  const title = useEditor((s) => s.title);
  const events = useEditor((s) => everyEvent(s.hist.present).length);
  const mark = useWizard((s) => s.markPreview);
  const markPct = mark === null ? null : Math.round(mark * 100);
  const [own, setOwn] = useState(false);
  const [licence, setLicence] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const reviewId = server?.status === "review" ? server.id : null;
  useEffect(() => (reviewId ? watchReview(reviewId) : undefined), [reviewId]);
  if (!def) return null;

  if (!user || !user.nickname || !server) {
    return (
      <section className="submit-panel panel" aria-label="Submit">
        <p>
          This draft is saved on this device. <Link href="/signin?next=/write">Sign in</Link> to reserve a place in a
          day and submit it.
        </p>
      </section>
    );
  }

  const titleCheck = checkTitle(title);
  const date = dayToDate(def.day, epochMs);
  const freezeLocal = formatPlayTime(freezeAtMs(def.day, epochMs));

  if (server.status === "review") {
    return (
      <section className="submit-panel panel" aria-label="Submission" data-testid="submit-status" data-status="review">
        <h2>Passed the checks — waiting for review</h2>
        <p>
          New contributors' first songs are listened to by a person before they join the endless song. You'll get an
          email when it's decided, usually well before the day's lineup freezes ({freezeLocal}).
        </p>
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={() => navigate("/calendar")}>
            Write another song
          </button>
        </div>
      </section>
    );
  }

  if (server.status === "approved") {
    return (
      <section
        className="submit-panel panel ok"
        aria-label="Submission"
        data-testid="submit-status"
        data-status="approved"
      >
        <h2>Approved — your song is part of the endless song</h2>
        {server.playsAt ? (
          <p data-testid="plays-at">Your song plays {formatPlayTime(server.playsAt)} (your local time).</p>
        ) : (
          <p>
            It plays on {date}. The exact time is fixed when the day's lineup freezes ({freezeLocal}).
          </p>
        )}
        {server.wizardShare !== null && server.wizardShare > 0 && (
          <p className="wizard-badge">✨ Marked {Math.round(server.wizardShare * 100)} % Wizard</p>
        )}
        <div className="row">
          <button type="button" className="btn btn-primary" onClick={() => navigate("/calendar")}>
            Write another song
          </button>
          {server.loopId && (
            <Link className="btn" href={`/loop/${server.loopId}`}>
              Loop page
            </Link>
          )}
        </div>
      </section>
    );
  }

  return (
    <section
      className="submit-panel panel"
      aria-label="Submit your song"
      data-testid="submit-status"
      data-status={server.status}
    >
      {server.sentOn && (
        <p className="notice">That day was full or at your limit, so you're writing for {date} instead.</p>
      )}
      {server.status === "rejected" && server.check && (
        <div className="rejected" role="alert">
          <h2>Not accepted yet — here's what to fix</h2>
          <ul data-testid="reasons">
            {server.check.results
              .filter((r) => !r.passed)
              .map((r) => (
                <li key={r.rule}>{r.reason}</li>
              ))}
          </ul>
          <p className="muted">
            Change your loop and submit again — you can keep trying until the day freezes ({freezeLocal}).
          </p>
        </div>
      )}
      {server.status === "checking" ? (
        <p role="status" data-testid="checking">
          Checking your loop… results usually arrive within seconds.
        </p>
      ) : (
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            setError("");
            setBusy(true);
            try {
              await submitServerDraft();
            } catch (err) {
              setError(err instanceof ApiError ? err.message : "Couldn't submit.");
            } finally {
              setBusy(false);
            }
          }}
        >
          <label className="stack-label">
            <span>Title (optional, up to 40 characters)</span>
            <input
              type="text"
              value={title}
              maxLength={40}
              onChange={(e) => useEditor.getState().set({ title: e.target.value })}
              onBlur={() => void saveServerDraft().catch(() => undefined)}
              data-testid="loop-title"
            />
          </label>
          {!titleCheck.ok && <p className="error">{titleCheck.reason}</p>}
          <label className="toggle">
            <input type="checkbox" checked={own} onChange={(e) => setOwn(e.target.checked)} data-testid="decl-own" />
            <span>This song is my own work.</span>
          </label>
          <label className="toggle">
            <input
              type="checkbox"
              checked={licence}
              onChange={(e) => setLicence(e.target.checked)}
              data-testid="decl-licence"
            />
            <span>
              I license it under <Link href="/legal/licence">CC BY 4.0</Link> with credit to my nickname (
              {user.nickname}).
            </span>
          </label>
          {error && (
            <p role="alert" className="error">
              {error}
            </p>
          )}
          {markPct !== null && markPct > 0 && (
            <p className="wizard-badge">✨ This loop will be marked: {markPct} % Wizard</p>
          )}
          <div className="row">
            <button
              className="btn btn-primary"
              disabled={!own || !licence || busy || events === 0 || !titleCheck.ok}
              data-testid="submit"
            >
              Submit for {date}
            </button>
            {server.reservedUntil && (
              <span className="muted small">
                Place reserved until {new Date(server.reservedUntil).toLocaleTimeString()}
              </span>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
